import { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { pierValidationSchema } from '@/utils/validations';
import usePagination from '@/hooks/usePagination';
import { ErrorApiResponse } from '@/types/api.types';
import { ConfirmationDialog } from '@/components/dialogBox';
import {
  PierListQueryParams,
  IPierData,
  IPierFormData,
} from '@/types/pier-management.types';
import {
  useGetPiersQuery,
  useCreatePierMutation,
  useUpdatePierMutation,
  useDeletePierMutation,
  useGetPierByIdQuery,
  useUploadPierImageMutation,
  useRemovePierImageMutation,
} from '@/services/PierManagementService';
import { useGetHarborsQuery } from '@/services/HarborManagementService';
import { PierModal } from './PierModal';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { HarborOption } from '../booking-management/BookingListings';
import Select from 'react-select';
import { useLocation } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import {
  CreatePierPayload,
  PierContentTranslations,
  UpdatePierPayload,
  rawImageCaptionsToDto,
} from '@/types/pier-management.types';
import { usePermissions } from '@/hooks/usePermissions';
import { sortByNaturalString } from '@/utils/naturalSort';

interface PierListingsProps {
  harborId?: string;
}

const serializeTranslations = (
  translations?: PierContentTranslations | null
): PierContentTranslations | null => {
  const result: PierContentTranslations = {};
  (['fi', 'sv', 'en', 'de'] as const).forEach((language) => {
    result[language] = translations?.[language]?.trim() || null;
  });
  return result;
};

const PierListings = ({ harborId }: PierListingsProps) => {
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const { hasPermission } = usePermissions();
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    pier: IPierData | null;
    mode: 'view' | 'edit' | 'create';
  }>({ isOpen: false, pier: null, mode: 'view' });
  const [confirmDeleteState, setConfirmDeleteState] = useState<{
    isOpen: boolean;
    pierId: string | null;
  }>({ isOpen: false, pierId: null });
  const [formData, setFormData] = useState<IPierFormData>({
    harbor_id: harborId || '',
    name: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { enqueueSnackbar } = useSnackbar();
  const [
    updatePier,
    { isLoading: isUpdating, isSuccess: updateSuccess, isError: updateError },
  ] = useUpdatePierMutation();
  const [deletePier] = useDeletePierMutation();
  const [createPier, { isLoading: isCreating }] = useCreatePierMutation();
  const [uploadPierImage, { isLoading: isUploadingImage }] =
    useUploadPierImageMutation();
  const [removePierImage, { isLoading: isRemovingImage }] =
    useRemovePierImageMutation();
  const [harborOptions, setHarborOptions] = useState<HarborOption[]>([]);
  const [pierIdToFetch, setPierIdToFetch] = useState<string | null>(null);
  const selectedHarborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );
  const location = useLocation();
  const isHarborDetailsRoute = location.pathname === '/harbors/details';
  const [selectedHarbor, setSelectedHarbor] = useState<string>(
    selectedHarborId && isHarborDetailsRoute ? selectedHarborId : ''
  );
  const [harborPagination, setHarborPagination] = useState({
    page: 1,
    limit: 10,
  });

  const { data: harborsData, isLoading: isHarborsLoading } = useGetHarborsQuery(
    {}
  );

  const {
    data: pierResponse,
    isSuccess: pierFetchSuccess,
    error: pierFetchError,
    isFetching: pierFetching,
    refetch: refetchPier,
  } = useGetPierByIdQuery(pierIdToFetch!, {
    skip: !pierIdToFetch,
  }) as {
    data: IPierData | undefined;
    isSuccess: boolean;
    error: unknown;
    isFetching: boolean;
    refetch: () => void;
  };

  useEffect(() => {
    if (harborsData?.data) {
      const options = harborsData.data.map((harbor) => ({
        value: harbor.id,
        label: harbor.name,
      }));
      setHarborOptions(options);
    }
  }, [harborsData]);

  useEffect(() => {
    if (pierFetchSuccess && pierResponse) {
      const selectedHarbor = harborsData?.data.find(
        (harbor) => harbor.id === pierResponse.harbor_id
      );
      const pierData: IPierData = {
        id: pierResponse.id,
        harbor_id: pierResponse.harbor_id,
        name: pierResponse.name,
        boater_label: pierResponse.boater_label,
        approach_note: pierResponse.approach_note,
        boater_label_translations: pierResponse.boater_label_translations,
        approach_note_translations: pierResponse.approach_note_translations,
        capacity_mode: pierResponse.capacity_mode,
        pooled_capacity: pierResponse.pooled_capacity,
        image: pierResponse.image,
        image_captions: pierResponse.image_captions,
        harbor: selectedHarbor
          ? {
              id: selectedHarbor.id,
              name: selectedHarbor.name,
            }
          : undefined,
        berths: pierResponse.berths, // Add berths to pierData
      };

      setFormData({
        harbor_id: pierData.harbor_id,
        name: pierData.name,
        boater_label: pierData.boater_label,
        approach_note: pierData.approach_note,
        boater_label_translations: pierData.boater_label_translations,
        approach_note_translations: pierData.approach_note_translations,
        capacity_mode: pierData.capacity_mode,
        pooled_capacity: pierData.pooled_capacity,
        image: pierData.image ?? undefined,
        image_captions: rawImageCaptionsToDto(pierData.image_captions),
        harbor: pierData.harbor,
      });
      setErrors({});
      setModalState((prev) => ({
        ...prev,
        isOpen: true,
        pier: pierData,
      }));
    }
  }, [pierFetchSuccess, pierResponse, harborsData]);

  useEffect(() => {
    if (pierFetchError) {
      enqueueSnackbar('Failed to fetch pier details', {
        variant: 'error',
      });
    }
  }, [pierFetchError, enqueueSnackbar]);

  const handleHarborChange = (selectedOption: HarborOption | null) => {
    const harborId = selectedOption ? selectedOption.value : '';
    setSelectedHarbor(harborId);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const queryParams: PierListQueryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue || undefined,
    ...(selectedHarbor && { harbor_id: selectedHarbor }),
    ...(harborId && !selectedHarbor && { harbor_id: harborId }),
  };

  const {
    data: piersData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetPiersQuery(queryParams);

  useEffect(() => {
    if (typeof refetch === 'function') {
      refetch();
    }
  }, [debouncedValue, selectedHarbor, refetch]);

  useEffect(() => {
    if (piersData) {
      handleTotalPages(piersData.total_pages ?? piersData.totalPages ?? 1);
      handleTotalItems(piersData.total_items ?? piersData.total ?? 0);
    }
  }, [piersData, handleTotalPages, handleTotalItems]);

  const handleCloseModal = useCallback(() => {
    setModalState({ isOpen: false, pier: null, mode: 'view' });
    setFormData({ name: '', harbor_id: '', harbor: undefined });
    setErrors({});
    setPierIdToFetch(null);
    refetch();
  }, [setModalState, setFormData, setErrors, setPierIdToFetch, refetch]);

  // Set synchronously before `updatePier` resolves whenever an image change
  // still needs to be synced, so the updateSuccess effect below doesn't close
  // the modal out from under the in-flight image upload/remove call.
  const [isSyncingImage, setIsSyncingImage] = useState(false);

  useEffect(() => {
    if (updateSuccess && !isSyncingImage) {
      handleCloseModal();
      refetch();
    }
  }, [updateSuccess, isSyncingImage, enqueueSnackbar, refetch, handleCloseModal]);

  useEffect(() => {
    if (updateError) {
      enqueueSnackbar('Failed to update pier', { variant: 'error' });
    }
  }, [updateError, enqueueSnackbar]);

  const handleOpenModal = useCallback(
    (pier: IPierData | null, mode: 'view' | 'edit' | 'create') => {
      if (mode === 'create') {
        setModalState({ isOpen: true, pier: null, mode: 'create' });
        setFormData({
          harbor_id: harborId || selectedHarborId || '',
          name: '',
        });
        setErrors({});
        refetch();
      } else if (mode === 'edit' || mode === 'view') {
        if (pier?.id) {
          setModalState({ isOpen: true, pier, mode });
          setFormData({
            name: pier.name || '',
            harbor_id: pier.harbor_id || '',
            harbor: pier.harbor,
          });
          setErrors({});
          setPierIdToFetch(pier.id);
        }
      }
    },
    [harborId, selectedHarborId, refetch]
  );

  const handleDelete = async () => {
    if (confirmDeleteState.pierId) {
      try {
        await deletePier(confirmDeleteState.pierId).unwrap();
        enqueueSnackbar('Pier deleted successfully', {
          variant: 'success',
        });
        setConfirmDeleteState({ isOpen: false, pierId: null });
        refetch();
      } catch {
        enqueueSnackbar('Failed to delete pier', { variant: 'error' });
      }
    }
  };

  const handleSubmit = async () => {
    try {
      await pierValidationSchema.validate(formData, {
        abortEarly: false,
      });

      if (modalState.mode === 'create') {
        const payload: CreatePierPayload = {
          name: formData.name,
          harbor_id: formData.harbor_id,
          boater_label_translations: serializeTranslations(
            formData.boater_label_translations
          ),
          approach_note_translations: serializeTranslations(
            formData.approach_note_translations
          ),
          capacity_mode: formData.capacity_mode || undefined,
          pooled_capacity:
            formData.capacity_mode === 'POOLED' ||
            formData.capacity_mode === 'SINGLE_BERTH_POOL'
              ? formData.pooled_capacity
              : undefined,
        };
        const created = await createPier(payload).unwrap();

        if (formData.image instanceof File) {
          try {
            await uploadPierImage({
              pier_id: created.data.id,
              file: formData.image,
              captions: formData.image_captions,
            }).unwrap();
          } catch {
            enqueueSnackbar(
              'Pier created, but image upload failed. Edit the pier to add an image.',
              { variant: 'error' }
            );
            handleCloseModal();
            refetch();
            return;
          }
        }

        enqueueSnackbar('Pier created successfully', {
          variant: 'success',
        });
        handleCloseModal();
        refetch();
      } else if (modalState.mode === 'edit' && modalState.pier) {
        const payload = {
          name: formData.name,
          harbor_id: formData.harbor_id,
          pierId: pierIdToFetch!,
          boater_label_translations: serializeTranslations(
            formData.boater_label_translations
          ),
          approach_note_translations: serializeTranslations(
            formData.approach_note_translations
          ),
          capacity_mode: formData.capacity_mode || undefined,
          pooled_capacity:
            formData.capacity_mode === 'POOLED' ||
            formData.capacity_mode === 'SINGLE_BERTH_POOL'
              ? formData.pooled_capacity
              : undefined,
        } as UpdatePierPayload;

        const originalCaptions = rawImageCaptionsToDto(
          modalState.pier?.image_captions
        );
        // formData.image is left as either the original string URL or
        // (on first load) undefined when the user hasn't touched the image
        // control - both mean "keep the existing image", as opposed to a
        // File (new upload) or null (removal).
        const imageUnchanged =
          !(formData.image instanceof File) && formData.image !== null;
        const captionsChanged =
          imageUnchanged &&
          JSON.stringify(formData.image_captions) !==
            JSON.stringify(originalCaptions);
        const hasImageChange =
          formData.image instanceof File ||
          (formData.image === null && !!modalState.pier?.image) ||
          captionsChanged;
        if (hasImageChange) setIsSyncingImage(true);

        await updatePier(payload).unwrap();

        try {
          if (formData.image instanceof File) {
            await uploadPierImage({
              pier_id: pierIdToFetch!,
              file: formData.image,
              captions: formData.image_captions,
            }).unwrap();
          } else if (formData.image === null && modalState.pier?.image) {
            await removePierImage(pierIdToFetch!).unwrap();
          } else if (captionsChanged && modalState.pier?.image) {
            await uploadPierImage({
              pier_id: pierIdToFetch!,
              captions: formData.image_captions,
            }).unwrap();
          }
        } catch {
          enqueueSnackbar('Pier updated, but the image change failed.', {
            variant: 'error',
          });
          setIsSyncingImage(false);
          handleCloseModal();
          refetch();
          return;
        }

        setIsSyncingImage(false);
        enqueueSnackbar('Pier updated successfully', {
          variant: 'success',
        });
        handleCloseModal();
        refetch();
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar(
          `Please fix the form errors (${err.inner.length} errors occurred)`,
          { variant: 'error' }
        );
      } else {
        const errorMessage =
          (err as ErrorApiResponse).data?.message || 'Failed to save pier';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const resetFilters = () => {
    if (searchTerm || selectedHarbor) {
      resetSearch();
      setSelectedHarbor(
        selectedHarborId && isHarborDetailsRoute ? selectedHarborId : ''
      );
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };

  const columns: ColumnDef<IPierData>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Pier Name',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            onClick={() => handleOpenModal(row.original, 'view')}
            className="text-sm font-medium text-gray-900 hover:text-primary-active focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label={`View ${row.original.name}`}
            data-testid={`view-pier-${row.original.id}`}
          >
            {row.original.name}
          </button>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessorKey: 'berths',
        header: 'Berths',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-2">
            {row.original.berths && row.original.berths.length > 0 ? (
              sortByNaturalString(row.original.berths, (berth) => berth.berth_number).map((berth) => (
                <span
                  key={berth.id}
                  className="inline-block bg-blue-100 dark:bg-blue-900 contrast:bg-blue-900 text-blue-800 dark:text-blue-200 contrast:text-blue-200 text-xs font-medium px-2.5 py-0.5 rounded-full"
                >
                  {berth.berth_number}
                </span>
              ))
            ) : (
              <span className="text-sm text-gray-700">-</span>
            )}
          </div>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {hasPermission('berth_pier_management_read') && (
              <button
                type="button"
                onClick={() => handleOpenModal(row.original, 'view')}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="View"
                aria-label={`View ${row.original.name}`}
                data-testid={`view-action-${row.original.id}`}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-500" />
              </button>
            )}
            {hasPermission('berth_pier_management_update') && (
              <button
                type="button"
                onClick={() => handleOpenModal(row.original, 'edit')}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Edit"
                aria-label={`Edit ${row.original.name}`}
                data-testid={`edit-action-${row.original.id}`}
              >
                <KeenIcon
                  type="filled"
                  icon="pencil"
                  className="text-gray-500"
                />
              </button>
            )}
            {hasPermission('berth_pier_management_delete') && (
              <button
                type="button"
                onClick={() =>
                  setConfirmDeleteState({
                    isOpen: true,
                    pierId: row.original.id,
                  })
                }
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Delete"
                aria-label={`Delete ${row.original.name}`}
                data-testid={`delete-action-${row.original.id}`}
              >
                <KeenIcon type="filled" icon="trash" className="text-red-500" />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [handleOpenModal, hasPermission]
  );

  const handleOpenCreateModal = () => {
    setModalState({
      isOpen: true,
      pier: null,
      mode: 'create',
    });
  };

  return (
    <div className="piers-listings card card-grid w-full">
      <div className="px-6 pt-5">
        <h1 className="text-base font-semibold text-gray-900">
          Piers Management
        </h1>
      </div>
      <div className="card-header piers-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full justify-between">
          <div className="piers-header-left flex items-center gap-2">
            <div className="flex-shrink-0">
              <label
                className="input input-sm h-8 sm:h-10 flex items-center"
                htmlFor="search-pier"
                aria-label="Search piers"
              >
                <KeenIcon
                  type="filled"
                  icon="magnifier"
                  className="text-gray-500"
                />
                <input
                  id="search-pier"
                  placeholder="Search piers"
                  type="text"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                  aria-label="Search piers by name"
                  data-testid="search-input"
                />
              </label>
            </div>
            <div className="flex-shrink-0 w-full sm:w-64">
              <Select
                id="harbor-filter"
                options={harborOptions}
                value={
                  harborOptions.find(
                    (option) => option.value === selectedHarbor
                  ) || null
                }
                onChange={handleHarborChange}
                isDisabled={
                  (!!selectedHarborId && isHarborDetailsRoute) ||
                  isLoading ||
                  isFetching ||
                  isUpdating ||
                  isHarborsLoading
                }
                placeholder={
                  isHarborsLoading ? 'Loading harbors...' : 'Select harbor'
                }
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '0.5rem',
                    borderColor: '#d1d5db',
                    minHeight: '2.5rem',
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: '#93c5fd',
                    },
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 50,
                    marginTop: '0.5rem',
                    borderRadius: '0.375rem',
                    boxShadow:
                      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: '0.875rem',
                    backgroundColor: state.isSelected
                      ? '#e0f2fe'
                      : state.isFocused
                        ? '#f3f4f6'
                        : 'white',
                    color: '#1f2937',
                    padding: '0.5rem 0.75rem',
                    cursor: 'pointer',
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#9ca3af',
                  }),
                }}
                aria-label="Filter piers by harbor"
              />
              {(harborsData?.total_pages || 0) > 1 && (
                <div className="flex justify-between mt-1">
                  <button
                    disabled={harborPagination.page === 1}
                    onClick={() =>
                      setHarborPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    className="btn btn-xs"
                  >
                    Previous
                  </button>
                  <span className="text-xs">
                    Page {harborPagination.page} of{' '}
                    {harborsData?.total_pages || 1}
                  </span>
                  <button
                    disabled={
                      harborPagination.page >= (harborsData?.total_pages || 1)
                    }
                    onClick={() =>
                      setHarborPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    className="btn btn-xs"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            {isHarborDetailsRoute &&
              hasPermission('berth_pier_management_create') && (
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={handleOpenCreateModal}
                  aria-label="Add new pier"
                  data-testid="add-new-pier"
                  style={{ display: isHarborDetailsRoute ? undefined : 'none' }}
                >
                  Add New Pier
                </button>
              )}
          </div>
        </div>
        {searchTerm && searchTerm.trim() !== '' && (
          <button
            className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
            onClick={resetFilters}
            aria-label="Clear all filters"
            data-testid="reset-search"
          >
            <KeenIcon
              type="solid"
              icon="arrows-circle"
              className="text-gray-600"
            />
            Clear Filters
          </button>
        )}
      </div>
      <div className="card-body">
        {isLoading || isFetching || pierFetching ? (
          <div className="text-center py-4" role="status" aria-live="polite">
            Loading piers...
          </div>
        ) : isError ? (
          <div className="text-center py-4 text-red-500" role="alert">
            Failed to load piers. Please try again.
          </div>
        ) : (
          <div className="piers-grid-wrapper">
            <DataGrid
              columns={columns}
              data={piersData?.data || []}
              rowSelect={false}
              loading={isLoading || isFetching || pierFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems,
                totalPages,
                setPagination,
                info: `{from} - {to} of {count}`,
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'name', desc: false }]}
              serverSide={true}
              hasActiveFilters={!!searchTerm || !!selectedHarbor}
              onFilterReset={resetFilters}
              aria-label="Pier listings table"
              data-testid="pier-table"
            />
          </div>
        )}
        <PierModal
          open={modalState.isOpen}
          onClose={handleCloseModal}
          mode={modalState.mode}
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          setErrors={setErrors}
          onSubmit={handleSubmit}
          isSubmitting={
            isUpdating ||
            isCreating ||
            isUploadingImage ||
            isRemovingImage
          }
          pierData={modalState.pier}
          refetch={refetchPier}
        />
        <ConfirmationDialog
          open={confirmDeleteState.isOpen}
          onClose={() => setConfirmDeleteState({ isOpen: false, pierId: null })}
          onConfirm={handleDelete}
          message="Are you sure you want to delete this pier?"
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="delete-confirmation-title"
          aria-describedby="delete-confirmation-description"
          data-testid="delete-confirmation-dialog"
        />
      </div>
    </div>
  );
};

export { PierListings };
